import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Zap, FileText, Clock, User, Phone, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { VoiceFabricTrace } from '../components/voicefabric/VoiceFabricTrace';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DetailItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2.5">
    <Icon className="w-4 h-4 text-white/30 mt-0.5 shrink-0" />
    <div className="min-w-0">
      <p className="text-white/40 text-[10px] uppercase tracking-wider">{label}</p>
      <p className="text-white text-sm truncate" title={value}>{value || '—'}</p>
    </div>
  </div>
);

const CallVoiceFabricPage = () => {
  const { callId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!callId || !token) return;
    axios.get(`${API}/calls/${callId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setCall(r.data))
      .catch(() => setCall(null))
      .finally(() => setLoading(false));
  }, [callId, token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0F1C] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-teal-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0F1C] text-white">
      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="text-white/50 hover:text-white"
          data-testid="trace-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
        </Button>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <div className="flex items-start gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-yellow-500/20 flex items-center justify-center">
              <Zap className="w-5 h-5 text-yellow-400" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-white">VoiceFabric Trace</h1>
              <p className="text-white/40 text-sm">
                Full audit trail for this call — every rule that ran, its match decision,
                extracted fields, and the dispatched outcome.
              </p>
            </div>
            {call && (
              <Link
                to={`/calls/${callId}`}
                className="text-xs px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 hover:border-teal-500/30 text-white/60 hover:text-white transition-colors"
                data-testid="trace-back-to-call"
              >
                Open call detail →
              </Link>
            )}
          </div>

          {call && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-white/10">
              <DetailItem icon={User} label="Agent" value={call.agent_name || call.agent_id} />
              <DetailItem icon={Phone} label="Customer" value={call.customer_phone} />
              <DetailItem icon={FileText} label="Type" value={(call.call_type || '').replace(/_/g, ' ')} />
              <DetailItem
                icon={Clock}
                label="When"
                value={call.call_date ? new Date(call.call_date).toLocaleString() : '—'}
              />
            </div>
          )}
        </div>

        <VoiceFabricTrace callId={callId} token={token} />
      </div>
    </div>
  );
};

export default CallVoiceFabricPage;
